import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Search,
  ChevronDown,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  X,
  Download,
  Mail,
  MoreHorizontal,
  Loader2,
} from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import StatusBadge from "@/components/admin/StatusBadge";
import SubscriberDetailDrawer from "@/components/admin/SubscriberDetailDrawer";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import type { Database } from "@/integrations/supabase/types";

type Row = Database["public"]["Tables"]["newsletter_subscribers"]["Row"];
type SourceFilter = Database["public"]["Enums"]["subscriber_source"] | "all";
type StatusFilter = "all" | "active" | "pending" | "unsubscribed";
type SortDir = "asc" | "desc";

const sourceLabels: Record<string, string> = {
  footer: "Footer",
  newsletter_page: "Newsletter page",
  post_cta: "Post CTA",
};

const sourceOptions: { value: SourceFilter; label: string }[] = [
  { value: "all", label: "All sources" },
  { value: "footer", label: "Footer" },
  { value: "newsletter_page", label: "Newsletter page" },
  { value: "post_cta", label: "Post CTA" },
];

const statusOptions: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "pending", label: "Pending" },
  { value: "unsubscribed", label: "Unsubscribed" },
];

const subscriberStatus = (
  r: { confirmed_at: string | null; unsubscribed_at: string | null },
): "active" | "pending" | "unsubscribed" => {
  if (r.unsubscribed_at) return "unsubscribed";
  if (r.confirmed_at) return "active";
  return "pending";
};

const fmtDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

const toCsv = (rows: Row[]) => {
  const headers = [
    "email",
    "first_name",
    "role_title",
    "organization",
    "organization_size",
    "interests",
    "source",
    "status",
    "subscribed_at",
    "confirmation_sent_at",
    "confirmed_at",
    "unsubscribed_at",
  ];
  const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const lines = [headers.join(",")];
  for (const r of rows) {
    lines.push(
      [
        escape(r.email),
        escape(r.first_name ?? ""),
        escape(r.role_title ?? ""),
        escape(r.organization ?? ""),
        escape(r.organization_size ?? ""),
        escape((r.interests ?? []).join("; ")),
        escape(r.source),
        escape(subscriberStatus(r)),
        escape(r.created_at ?? ""),
        escape((r as Row & { confirmation_sent_at?: string | null }).confirmation_sent_at ?? ""),
        escape(r.confirmed_at ?? ""),
        escape(r.unsubscribed_at ?? ""),
      ].join(",")
    );
  }
  return lines.join("\n");
};

const AdminSubscribers = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [source, setSource] = useState<SourceFilter>("all");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  const load = async () => {
    setLoading(true);
    let q = supabase
      .from("newsletter_subscribers")
      .select("*")
      .order("created_at", { ascending: sortDir === "asc" });

    if (source !== "all") q = q.eq("source", source);
    if (status === "active") q = q.not("confirmed_at", "is", null).is("unsubscribed_at", null);
    if (status === "pending") q = q.is("confirmed_at", null).is("unsubscribed_at", null);
    if (status === "unsubscribed") q = q.not("unsubscribed_at", "is", null);
    const term = debounced.trim();
    if (term) {
      const safe = term.replace(/[,()*]/g, " ").trim();
      if (safe) {
        q = q.or(
          `email.ilike.%${safe}%,first_name.ilike.%${safe}%,organization.ilike.%${safe}%`,
        );
      }
    }

    const { data, error } = await q;
    if (error) {
      toast({
        title: "Failed to load subscribers",
        description: error.message,
        variant: "destructive",
      });
    }
    setRows((data ?? []) as Row[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, status, debounced, sortDir]);

  const hasFilters =
    search.trim() !== "" || source !== "all" || status !== "all";
  const clearFilters = () => {
    setSearch("");
    setSource("all");
    setStatus("all");
  };

  const counts = useMemo(() => {
    let active = 0, pending = 0, unsub = 0;
    for (const r of rows) {
      const s = subscriberStatus(r);
      if (s === "active") active++;
      else if (s === "pending") pending++;
      else unsub++;
    }
    return { active, pending, unsub };
  }, [rows]);

  const handleExport = () => {
    const csv = toCsv(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleUnsubscribe = async (id: string) => {
    setUpdatingId(id);
    const { error } = await supabase
      .from("newsletter_subscribers")
      .update({ unsubscribed_at: new Date().toISOString() })
      .eq("id", id);
    setUpdatingId(null);
    if (error) {
      toast({
        title: "Failed to update",
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    toast({ title: "Marked as unsubscribed" });
    load();
  };

  const handleResubscribe = async (id: string) => {
    setUpdatingId(id);
    const { error } = await supabase
      .from("newsletter_subscribers")
      .update({ unsubscribed_at: null })
      .eq("id", id);
    setUpdatingId(null);
    if (error) {
      toast({
        title: "Failed to update",
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    toast({ title: "Re-activated" });
    load();
  };

  const handleMarkConfirmed = async (id: string) => {
    setUpdatingId(id);
    const { error } = await supabase
      .from("newsletter_subscribers")
      .update({ confirmed_at: new Date().toISOString(), unsubscribed_at: null })
      .eq("id", id);
    setUpdatingId(null);
    if (error) {
      toast({ title: "Failed to update", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Marked as confirmed" });
    load();
  };

  const handleResendConfirmation = async (row: Row) => {
    setUpdatingId(row.id);
    const { error } = await supabase.functions.invoke("subscribe-newsletter", {
      body: {
        email: row.email,
        source: row.source,
        locale: (row as Row & { locale?: string | null }).locale === "sv" ? "sv" : "en",
        first_name: row.first_name ?? undefined,
        role_title: row.role_title ?? undefined,
        organization: row.organization ?? undefined,
        organization_size: row.organization_size ?? undefined,
        interests: row.interests ?? undefined,
      },
    });
    setUpdatingId(null);
    if (error) {
      toast({ title: "Failed to resend", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Confirmation email resent" });
    load();
  };

  const handleDelete = async (id: string, email: string) => {
    if (!window.confirm(`Permanently delete ${email}? This cannot be undone.`)) return;
    setUpdatingId(id);
    const { error } = await supabase.from("newsletter_subscribers").delete().eq("id", id);
    setUpdatingId(null);
    if (error) {
      toast({ title: "Failed to delete", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Subscriber deleted" });
    load();
  };

  const handleSetLocale = async (id: string, locale: "en" | "sv") => {
    setUpdatingId(id);
    const { error } = await supabase
      .from("newsletter_subscribers")
      .update({ locale })
      .eq("id", id);
    setUpdatingId(null);
    if (error) {
      toast({ title: "Failed to update language", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: `Language set to ${locale.toUpperCase()}` });
    load();
  };

  const sourceLabel =
    source === "all"
      ? "Source"
      : sourceOptions.find((s) => s.value === source)?.label ?? "Source";
  const statusLabel =
    status === "all"
      ? "Status"
      : statusOptions.find((s) => s.value === status)?.label ?? "Status";

  return (
    <>
      <Helmet>
        <title>Subscribers · Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Subscribers</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {rows.length} {rows.length === 1 ? "subscriber" : "subscribers"}
            {" · "}
            {counts.active} active · {counts.pending} pending · {counts.unsub} unsubscribed
            {hasFilters && (
              <>
                {" · "}
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-1 text-foreground hover:underline"
                >
                  <X className="w-3 h-3" /> Clear filters
                </button>
              </>
            )}
          </p>
        </div>
        <Button variant="outline" onClick={handleExport} disabled={rows.length === 0}>
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </header>

      <div className="rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1100px]">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left px-4 py-2 font-medium w-[28%]">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search email, name, organization…"
                      className="pl-7 h-8 text-xs normal-case tracking-normal bg-background/60 border-border/60"
                    />
                  </div>
                </th>
                <th className="text-left px-4 py-2 font-medium">Name</th>
                <th className="text-left px-4 py-2 font-medium">Organization</th>
                <th className="text-left px-4 py-2 font-medium">Interests</th>
                <th className="text-left px-4 py-2 font-medium">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className={cn(
                          "inline-flex items-center gap-1 hover:text-foreground transition-colors",
                          source !== "all" && "text-foreground"
                        )}
                      >
                        {sourceLabel}
                        <ChevronDown className="w-3 h-3" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {sourceOptions.map((opt) => (
                        <DropdownMenuItem
                          key={opt.value}
                          onClick={() => setSource(opt.value)}
                        >
                          {opt.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </th>
                <th className="text-left px-4 py-2 font-medium">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className={cn(
                          "inline-flex items-center gap-1 hover:text-foreground transition-colors",
                          status !== "all" && "text-foreground"
                        )}
                      >
                        {statusLabel}
                        <ChevronDown className="w-3 h-3" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {statusOptions.map((opt) => (
                        <DropdownMenuItem
                          key={opt.value}
                          onClick={() => setStatus(opt.value)}
                        >
                          {opt.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </th>
                <th className="text-left px-4 py-2 font-medium whitespace-nowrap">
                  <button
                    onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                    className="inline-flex items-center gap-1 hover:text-foreground transition-colors text-foreground"
                  >
                    Subscribed
                    {sortDir === "desc" ? (
                      <ArrowDown className="w-3 h-3" />
                    ) : (
                      <ArrowUp className="w-3 h-3" />
                    )}
                  </button>
                </th>
                <th className="text-right px-4 py-2 font-medium w-12"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-4 py-3"><Skeleton className="h-4 w-48" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-24" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-32" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-16" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-5 w-20" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-5 w-16" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-24" /></td>
                    <td className="px-4 py-3"></td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <div className="p-10 flex flex-col items-center justify-center text-muted-foreground text-sm gap-3">
                      <Mail className="w-8 h-8 opacity-60" />
                      {hasFilters ? (
                        <>
                          <span>No subscribers match your filters.</span>
                          <Button variant="outline" size="sm" onClick={clearFilters}>
                            Clear filters
                          </Button>
                        </>
                      ) : (
                        <span>No subscribers yet.</span>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                <TooltipProvider delayDuration={150}>
                  {rows.map((r) => {
                    const st = subscriberStatus(r);
                    const isUnsub = st === "unsubscribed";
                    const isPending = st === "pending";
                    const rowStatusLabel = isUnsub ? "Unsubscribed" : isPending ? "Pending" : "Active";
                    const tone: "warn" | "success" | "info" = isUnsub ? "warn" : isPending ? "info" : "success";
                    const openDrawer = () => {
                      setSelectedId(r.id);
                      setDrawerOpen(true);
                    };
                    return (
                      <tr
                        key={r.id}
                        onClick={openDrawer}
                        className="border-t border-border hover:bg-muted/30 transition-colors cursor-pointer"
                      >
                        <td className="px-4 py-2.5 align-top">
                          <div className="font-medium text-foreground break-all">{r.email}</div>
                          {r.role_title && (
                            <div className="text-xs text-muted-foreground mt-0.5">{r.role_title}</div>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-muted-foreground align-top">
                          {r.first_name || <span className="text-muted-foreground/60">—</span>}
                        </td>
                        <td className="px-4 py-2.5 text-muted-foreground align-top">
                          {r.organization ? (
                            <>
                              <div className="text-foreground">{r.organization}</div>
                              {r.organization_size && (
                                <div className="text-xs text-muted-foreground mt-0.5">
                                  {r.organization_size}
                                </div>
                              )}
                            </>
                          ) : (
                            <span className="text-muted-foreground/60">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 align-top">
                          {r.interests && r.interests.length > 0 ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs text-foreground">
                                  {r.interests.length} selected
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-xs">
                                <div className="flex flex-col gap-0.5 text-xs">
                                  {r.interests.map((i) => (
                                    <span key={i}>{i.replace(/_/g, " ")}</span>
                                  ))}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <span className="text-muted-foreground/60">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-muted-foreground align-top">
                          {sourceLabels[r.source] ?? r.source}
                        </td>
                        <td className="px-4 py-2.5 align-top">
                          <StatusBadge label={rowStatusLabel} tone={tone} />
                        </td>
                        <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap align-top">
                          {fmtDate(r.created_at)}
                        </td>
                        <td
                          className="px-2 py-2.5 text-right align-top"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                {updatingId === r.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <MoreHorizontal className="w-4 h-4" />
                                )}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {isPending && (
                                <>
                                  <DropdownMenuItem onClick={() => handleResendConfirmation(r)}>
                                    Resend confirmation
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleMarkConfirmed(r.id)}>
                                    Mark as confirmed
                                  </DropdownMenuItem>
                                </>
                              )}
                              {isUnsub ? (
                                <DropdownMenuItem onClick={() => handleResubscribe(r.id)}>
                                  Re-activate
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => handleUnsubscribe(r.id)}>
                                  Mark unsubscribed
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={() => handleDelete(r.id, r.email)}
                                className="text-destructive focus:text-destructive"
                              >
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </TooltipProvider>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <SubscriberDetailDrawer
        subscriber={rows.find((r) => r.id === selectedId) ?? null}
        open={drawerOpen}
        onOpenChange={(o) => {
          setDrawerOpen(o);
          if (!o) setSelectedId(null);
        }}
        updating={updatingId === selectedId}
        onResendConfirmation={handleResendConfirmation}
        onMarkConfirmed={handleMarkConfirmed}
        onUnsubscribe={handleUnsubscribe}
        onResubscribe={handleResubscribe}
        onDelete={handleDelete}
        onSetLocale={handleSetLocale}
      />
    </>
  );
};

export default AdminSubscribers;
